import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Controller, Control } from "react-hook-form";
import Select, { components } from "react-select";
import { useTranslate } from "../../utils/hooks/useTranslate";
import { useLocation } from "react-router-dom";
import Icon from "../Icon/Icon";
import { IconColorE } from "../../models/enums";
import { customSearchCategorySelectStyles } from "./customSelectStyles";
import useApp from "../../utils/hooks/useApp";
import { SelectOptionT } from "../../models/models";
import {
  campaignCategoryOptions,
  categoryOptions,
} from "../../utils/helpers/categoryOptions";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentLang } from "../../store/selectors/appSelectors";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { RoutesE } from "../../routes/routesData";
import setLastValue from "./helpers/setLastValue";

type SearchCategorySelectorPropTypes = {
  control: Control<any>;
  value: SelectOptionT | null;
  onChange: (option: SelectOptionT) => void;
  isCampaignList?: boolean;
};

/**
 * Search category select UI and persistence (lastValue).
 * Parent Search owns value/sync from URL; this component renders the Select and persists choice.
 */
const SearchCategorySelector: FC<SearchCategorySelectorPropTypes> = ({
  control,
  value,
  onChange,
  isCampaignList = false,
}) => {
  const location = useLocation();
  const { t } = useTranslate();
  const { isRtlDirection } = useApp();
  const lang = useAppSelector(selectCurrentLang);
  const user = useAppSelector(selectCurrentUser);

  const options = useMemo(
    () =>
      isCampaignList ? campaignCategoryOptions : categoryOptions(user?.role),
    [isCampaignList, user?.role]
  );

  const DropdownIndicator = useCallback((props: any) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <Icon name="selectArrow" color={IconColorE.WHITE} />
        </components.DropdownIndicator>
      )
    );
  }, []);

  useEffect(() => {
    if (value?.value) {
      setLastValue(value.value, location.pathname === RoutesE.CAMPAIGNS);
    }
  }, [value?.value, location.pathname]);

  return (
    <Controller
      name="category"
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          options={options}
          isSearchable={false}
          getOptionLabel={({ label }: SelectOptionT) => t(label)}
          styles={{
            ...customSearchCategorySelectStyles(lang),
            menuList: (provided) => ({
              ...provided,
              minHeight: isCampaignList
                ? 66
                : options.length * 29 + 10,
              overflow: "hidden",
            }),
          }}
          components={{ DropdownIndicator }}
          placeholder={t("Search by")}
          isRtl={isRtlDirection}
          value={value}
          onChange={(option) => onChange(option as SelectOptionT)}
        />
      )}
    />
  );
};

export default SearchCategorySelector;
